export type WindParams = {
    velocitySpeed: number,
    maxWindIntensity: number,
    maxParticleAge: number,
    particleLineWidthMultiplier: number,
    particleMultiplier: number,
    boundary: number,
    colorScale: Array<string>,
};

export type WindParamKey = keyof WindParams;

export const defaultWindParams: WindParams = {
    velocitySpeed: 0.0015,
    maxWindIntensity: 25,
    maxParticleAge: 200,
    particleLineWidthMultiplier: 12.5,
    particleMultiplier: .004,
    boundary: 0.45,
    colorScale: [
        "#b18cf3",
        "#874efe",
        "#5e30eb",
        "#4d22b3",
        "#371a94",
        "#2c1376",
        "#1a0a53",
        "#11053b",
        "#000000",
    ],
};

type Props = {
    windParams: WindParams,
    setWindParams: React.Dispatch<React.SetStateAction<WindParams>>,
    expandedRightBar: boolean,
};

export function WindParamsPanel( {
    windParams,
    setWindParams,
    expandedRightBar,
}: Props )
{
    const numericRegex = /^-?\d*\.?\d*$/;

    const updateParam = ( key: WindParamKey, value: number | string ) =>
    {
        setWindParams( p => ( { ...p, [key]: value } ) );
    };

    const updateColor = ( i: number, value: string ) =>
    {
        const next = [ ...windParams.colorScale ];
        next[i] = value;
        setWindParams( p => ( { ...p, colorScale: next } ) );
    };

    const addColor = () => setWindParams( p => ( {
        ...p,
        colorScale: [ ...p.colorScale, "#ffffff" ],
    } ) );

    const removeColor = ( i: number ) => setWindParams( p => ( {
        ...p,
        colorScale: p.colorScale.filter( ( _, idx ) => idx !== i ),
    } ) );

    const numericFields: Array<[WindParamKey, string]> = [
        [ "velocitySpeed", "Velocity Speed" ],
        [ "maxWindIntensity", "Max Wind Intensity" ],
        [ "maxParticleAge", "Max Particle Age" ],
        [ "particleLineWidthMultiplier", "Particle Line Width Multiplier" ],
        [ "particleMultiplier", "Particle Multiplier" ],
    ];

    return (
        <div
            style = {{
                position: "absolute",
                top: 60,
                right: expandedRightBar ? 350 : 10,
                padding: 12,
                background: "rgba(255,255,255,0.9)",
                borderRadius: 8,
                width: 260,
                fontSize: 12,
                zIndex: 9999,
            }}
        >
            <h4 style = {{ marginTop: 0 }}>Windy Parameters</h4>

            {numericFields.map( ( [ key, label ] ) => (
                <label key = {key}>
                    {label}
                    <input
                        type = "text"
                        value = {windParams[key]}
                        onChange = {e =>
                        {
                            if ( numericRegex.test( e.target.value ) )
                            {
                                updateParam( key, e.target.value );
                            }
                        }}
                        onBlur = {() =>
                        {
                            const parsed = Number( windParams[key] );
                            if ( !isNaN( parsed ) ) updateParam( key, parsed );
                        }}
                    />
                </label>
            ) )}

            <div style = {{ marginTop: 12 }}>
                <strong>Color Scale</strong>

                {windParams.colorScale.map( ( c, i ) => (
                    <div
                        key = {i}
                        style = {{ display: "flex", alignItems: "center", marginTop: 6 }}
                    >
                        <input
                            type = "color"
                            value = {c}
                            onChange = {e => updateColor( i, e.target.value )}
                        />
                        <input
                            type = "text"
                            value = {c}
                            style = {{ marginLeft: 8, flex: 1 }}
                            onChange = {e => updateColor( i, e.target.value )}
                        />
                        <button onClick = {() => removeColor( i )}>✕</button>
                    </div>
                ) )}

                <button style = {{ marginTop: 8 }} onClick = {addColor}>
                    + Add Color
                </button>
            </div>
        </div>
    );
}
